import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Music, ExternalLink, Send, XCircle, Loader2, Smartphone, AlertTriangle } from 'lucide-react';
import {
  connectPreviewDevice,
  submitPreviewResult,
  type PreviewResult,
  type PreviewSession,
} from '../../services/previewDevice';
import { db } from '../../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { FirebaseError } from 'firebase/app';
import toast from 'react-hot-toast';
import { isAlreadyHandledError, shareLinkErrorMessage } from '../../utils/shareLinks';
import { LISTEN_PROVIDERS, playerLinkFromIds, previewPasteProblem } from '../../utils/playerLinks';
import { platformNameOf } from '../../utils/songLinks';
import type { PlayerProvider } from '../../services/playModes';

const DEFAULT_EVENT_PROVIDER: PlayerProvider = 'spotify';

function listenPlayerStorageKey(eventId: string): string {
  return `votebeats_preview_player_${eventId}`;
}

function readStoredListenProvider(eventId: string | undefined): PlayerProvider | null {
  if (!eventId) return null;
  try {
    const stored = sessionStorage.getItem(listenPlayerStorageKey(eventId));
    return LISTEN_PROVIDERS.find(provider => provider === stored) ?? null;
  } catch (storageError) {
    console.warn('Preview player choice could not be read', storageError);
    return null;
  }
}

function storeListenProvider(eventId: string | undefined, provider: PlayerProvider): void {
  if (!eventId) return;
  try {
    sessionStorage.setItem(listenPlayerStorageKey(eventId), provider);
  } catch (storageError) {
    console.warn('Preview player choice could not be saved', storageError);
  }
}

export default function PreviewPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [session, setSession] = useState<PreviewSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [pasteUrl, setPasteUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [chosenListenProvider, setChosenListenProvider] = useState<PlayerProvider | null>(() => readStoredListenProvider(eventId));
  const lastTrackIdRef = useRef<string | null>(null);

  // Connect on mount
  useEffect(() => {
    if (!eventId || !token) {
      setError('Invalid preview link');
      setLoading(false);
      return;
    }

    const connect = async () => {
      try {
        const sessionId = await connectPreviewDevice(eventId, token);
        const sessionSnap = await getDoc(doc(db, 'previewSessions', sessionId));
        if (!sessionSnap.exists()) {
          setError('Preview session not found or expired');
          return;
        }
        setSession({ id: sessionSnap.id, ...sessionSnap.data() } as PreviewSession);
      } catch (connectError) {
        console.error('Preview device connect failed', connectError);
        if (connectError instanceof FirebaseError && connectError.code === 'functions/permission-denied') {
          setError('Preview session not found or expired');
        } else {
          setError('Failed to connect');
        }
      } finally {
        setLoading(false);
      }
    };

    connect();
  }, [eventId, token]);

  // Poll for previewTrack changes
  useEffect(() => {
    if (!session?.id || !eventId) return;

    const poll = setInterval(async () => {
      try {
        const ref = doc(db, 'previewSessions', session.id);
        const snap = await getDoc(ref);
        if (!snap.exists()) return;
        const data = snap.data();

        const trackId = data.previewTrack?.requestId || null;
        if (trackId !== lastTrackIdRef.current) {
          lastTrackIdRef.current = trackId;
          setSession(prev => prev ? { ...prev, previewTrack: data.previewTrack || null, result: data.result || null } : prev);
          setPasteUrl('');
          setSubmitted(false);
        }
      } catch (pollError) {
        console.warn('Preview poll failed', pollError);
      }
    }, 2000);

    return () => clearInterval(poll);
  }, [session?.id, eventId]);

  const track = session?.previewTrack;
  const eventProvider = track?.eventProvider ?? DEFAULT_EVENT_PROVIDER;
  const eventName = platformNameOf(eventProvider);
  const acceptsLink = track?.acceptsLink ?? true;
  const listenProvider = chosenListenProvider ?? eventProvider;
  const listenName = platformNameOf(listenProvider);

  const handleChooseListenProvider = (provider: PlayerProvider) => {
    setChosenListenProvider(provider);
    storeListenProvider(eventId, provider);
  };

  const sendResult = async (result: PreviewResult): Promise<boolean> => {
    if (!eventId || !token) return false;
    setSubmitting(true);
    try {
      await submitPreviewResult(eventId, token, result);
      setSubmitted(true);
      return true;
    } catch (submitError) {
      console.error('Preview result submit failed', submitError);
      if (submitError instanceof FirebaseError && submitError.code === 'functions/invalid-argument') {
        toast.error(`Paste a full https:// ${eventName} link for the current song`);
      } else if (isAlreadyHandledError(submitError)) {
        toast.error("The DJ moved on to a different song, so this result wasn't sent");
      } else {
        toast.error(shareLinkErrorMessage(submitError, 'preview'));
      }
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitFound = async () => {
    if (!session?.previewTrack) return;
    const problem = previewPasteProblem(pasteUrl, eventProvider);
    if (problem) {
      toast.error(problem);
      return;
    }
    const sent = await sendResult({
      requestId: session.previewTrack.requestId,
      link: pasteUrl.trim(),
      status: 'found',
    });
    if (sent) setPasteUrl('');
  };

  const handleSubmitNotFound = async () => {
    if (!session?.previewTrack) return;
    await sendResult({
      requestId: session.previewTrack.requestId,
      link: '',
      status: 'not_found',
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-green-500 animate-spin" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className="text-lg font-medium text-gray-900 dark:text-white">{error}</p>
          <p className="text-sm text-gray-500 mt-2">Ask the DJ to generate a new QR code.</p>
        </div>
      </div>
    );
  }

  const listenLink = track ? playerLinkFromIds(track, track.trackIds ?? {}, listenProvider) : null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      {/* Status Bar */}
      <div className="bg-green-500 text-white px-4 py-2 flex items-center justify-center gap-2 text-sm font-medium">
        <Smartphone className="w-4 h-4" />
        Preview Device Connected
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-6">
        {!track ? (
          /* Waiting for track */
          <div className="text-center">
            <div className="w-20 h-20 mx-auto bg-gray-200 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
              <Music className="w-10 h-10 text-gray-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Waiting for next song...
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Keep this page open. Songs will appear here when the DJ sends them.
            </p>
          </div>
        ) : submitted ? (
          /* Submitted confirmation */
          <div className="text-center">
            <div className="w-20 h-20 mx-auto bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
              <Send className="w-10 h-10 text-green-500" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Sent!
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Waiting for next song...
            </p>
          </div>
        ) : listenLink && (
          /* Track to preview */
          <div className="w-full max-w-sm space-y-5">
            {/* Song info */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm text-center">
              <p className="text-lg font-bold text-gray-900 dark:text-white">{track.title}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{track.artist}</p>
            </div>

            {/* Player switcher */}
            <div className="space-y-2">
              <div className="grid grid-cols-3 gap-2" role="group" aria-label="Listen in">
                {LISTEN_PROVIDERS.map(provider => (
                  <button
                    key={provider}
                    type="button"
                    onClick={() => handleChooseListenProvider(provider)}
                    aria-pressed={provider === listenProvider}
                    className={`py-2 rounded-lg text-sm font-medium transition-colors ${
                      provider === listenProvider
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    {platformNameOf(provider)}
                  </button>
                ))}
              </div>
              {acceptsLink && listenProvider !== eventProvider && (
                <p className="text-xs text-amber-600 dark:text-amber-400 text-center">
                  Listening in {listenName}. The link you send back must be a {eventName} link.
                </p>
              )}
            </div>

            {/* Open in player button */}
            <a
              href={listenLink.url}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-3 py-4 bg-green-500 text-white rounded-xl text-lg font-semibold hover:bg-green-600 active:bg-green-700 transition-colors shadow-lg"
            >
              <ExternalLink className="w-6 h-6" />
              {listenLink.isDirect ? `Open in ${listenName}` : `Search in ${listenName}`}
            </a>

            {!acceptsLink && (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                Listening only. This event's player picks the track itself.
              </p>
            )}

            {/* Paste URL */}
            {acceptsLink && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Paste the {eventName} track link:
              </label>
              <input
                type="text"
                value={pasteUrl}
                onChange={(e) => setPasteUrl(e.target.value)}
                placeholder="https://…"
                className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
              />
              <button
                onClick={handleSubmitFound}
                disabled={submitting || !pasteUrl.trim()}
                className="w-full flex items-center justify-center gap-2 py-3 bg-green-500 text-white rounded-xl font-medium hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Submit Match
                  </>
                )}
              </button>
            </div>
            )}

            {/* Not Found */}
            {acceptsLink && (
            <button
              onClick={handleSubmitNotFound}
              disabled={submitting}
              className="w-full flex items-center justify-center gap-2 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl font-medium hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
            >
              <XCircle className="w-5 h-5" />
              Not Found
            </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
